// src/pages/BackupsPanel.jsx
// The user's backups: automatic ones, one before every deletion and restore, and any they ask for. Loaded when
// the panel is opened. Restoring puts the whole account back as it was then (after a backup of how it is now).
// With offline mode on there are two sets: the ones on this device and the ones on the server (which is where the
// version that lost a sync conflict is kept when this device's version was chosen).
import React, { useState } from 'react';
import { apiRequest } from '../api';
import { localEngineEnabled } from '../engine/engineFlag';
import { isGuestId } from '../guest';
import { localClient, serverClient } from '../sync/sync';
import { useSync } from '../sync/SyncProvider';
import { useConfirm } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { formatExact } from '../utils/time';

const REASONS = {
    automatic: 'Automatic',
    manual: 'Made by you',
    'before-delete': 'Before deleting a tournament',
    'before-restore': 'Before a restore',
    'before-sync': 'Before syncing with another device',
};

function BackupsPanel({ userId, onRestored }) {
    const confirm = useConfirm();
    const toast = useToast();
    const sync = useSync();
    const [open, setOpen] = useState(false);
    const [lists, setLists] = useState({}); // place -> array of backups, null while loading, 'unreachable'
    const [busy, setBusy] = useState(false);

    const places = localEngineEnabled() && !isGuestId(userId)
        ? [
            { key: 'device', title: 'On this device', call: localClient(userId), canCreate: true },
            { key: 'server', title: 'On the server', call: serverClient(userId), canCreate: false },
        ]
        : [{ key: 'main', title: null, call: (method, path, body) => apiRequest(method, path, userId, body), canCreate: true }];
    const twoPlaces = places.length > 1;

    const loadPlace = async (place) => {
        try {
            const list = await place.call('GET', '/backups');
            setLists((current) => ({ ...current, [place.key]: list }));
        } catch (error) {
            if (twoPlaces) {
                setLists((current) => ({ ...current, [place.key]: 'unreachable' }));
            } else {
                console.error('Error loading the backups:', error);
                toast.error('Could not load the backups.');
                setOpen(false);
            }
        }
    };
    const load = () => Promise.all(places.map(loadPlace));

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next) load();
    };

    const backUpNow = async (place) => {
        setBusy(true);
        try {
            await place.call('POST', '/backups', {});
            toast.success('Backup made.');
            await loadPlace(place);
        } catch (error) {
            toast.error(`Could not make a backup. ${error.message}`);
        }
        setBusy(false);
    };

    const restore = async (place, backup) => {
        const onServer = place.key === 'server';
        const confirmed = await confirm({
            title: 'Restore this backup?',
            message: `Everything (all your tournaments) goes back to how it was on ${formatExact(backup.createdAt)}. ` +
                'How it is now is backed up first, so this can be undone.' +
                (onServer ? ' It is restored on the server and then brought to this device.' : ''),
            confirmLabel: 'Restore',
            danger: false,
        });
        if (!confirmed) return;
        setBusy(true);
        try {
            await place.call('POST', `/backups/${backup.name}/restore`, {});
            if (onServer && sync.syncNow) await sync.syncNow(); // the device takes over what the server now has
            toast.success('Backup restored.');
            await load();
            onRestored?.();
        } catch (error) {
            toast.error(`Could not restore the backup. ${error.message}`);
        }
        setBusy(false);
    };

    const list = (place) => {
        const backups = lists[place.key];
        if (backups === 'unreachable') return <p className="hint">Not reachable right now (are you offline?).</p>;
        if (backups === undefined || backups === null) return <div className="skeleton" aria-busy="true" />;
        if (backups.length === 0) return <p className="hint">No backups yet.</p>;
        return (
            <ul className="backup-list">
                {backups.map((backup) => (
                    <li key={backup.name}>
                        <span>
                            <strong>{formatExact(backup.createdAt)}</strong>
                            <span className="muted"> · {REASONS[backup.reason] || backup.reason}</span>
                        </span>
                        <button className="btn btn-sm" onClick={() => restore(place, backup)} disabled={busy}>
                            Restore
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <section className="card backups-card" aria-label="Backups">
            <button className="section-toggle" aria-expanded={open} onClick={toggle}>
                <h2>Backups</h2>
            </button>
            {open && (
                <div className="backups-body">
                    <p className="hint">
                        Your data is backed up automatically (about every 6 hours, and before deleting a tournament,
                        restoring a backup or replacing data when syncing); the latest 30 are kept. They are stored next
                        to the data they protect, which guards against mistakes, not against losing the device or the
                        server.
                    </p>
                    {places.map((place) => (
                        <div key={place.key} className="backups-place">
                            {place.title && <h3>{place.title}</h3>}
                            {place.canCreate && (
                                <button className="btn btn-sm" onClick={() => backUpNow(place)} disabled={busy}>
                                    Back up now
                                </button>
                            )}
                            {list(place)}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

export default BackupsPanel;
